import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FiSave, FiCreditCard, FiToggleLeft, FiToggleRight, FiAlertCircle } from 'react-icons/fi';
import { PaymentsService } from '../services/payments.service';
import type { PaymentSettings, PayPalMode } from '../types/PaymentSettings';

const AdminPaymentSettingsPage: React.FC = () => {
  const { t } = useTranslation();

  const safeT = (key: string, fallback: string) => {
    const v = t(key);
    return v === key ? fallback : v;
  };

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [form, setForm] = useState<PaymentSettings>({
    paypal_enabled: true,
    paypal_client_id: '',
    paypal_merchant_id: '',
    paypal_mode: 'sandbox',

    bank_transfer_enabled: false,
    bank_holder_name: '',
    bank_name: '',
    bank_clabe: '',
    bank_reference_hint: '',

    updated_at: null
  });

  useEffect(() => {
    let alive = true;

    const load = async () => {
      setLoading(true);
      setError(null);
      setSuccess(null);
      try {
        const res = await PaymentsService.getAdminPaymentSettings();
        if (!alive) return;

        setForm({
          paypal_enabled: !!res.paypal_enabled,
          paypal_client_id: res.paypal_client_id ?? '',
          paypal_merchant_id: res.paypal_merchant_id ?? '',
          paypal_mode: (res.paypal_mode || 'sandbox') as PayPalMode,

          bank_transfer_enabled: !!res.bank_transfer_enabled,
          bank_holder_name: res.bank_holder_name ?? '',
          bank_name: res.bank_name ?? '',
          bank_clabe: res.bank_clabe ?? '',
          bank_reference_hint: res.bank_reference_hint ?? '',

          updated_at: res.updated_at ?? null
        });
      } catch {
        if (!alive) return;
        setError(safeT('payments.load_error', 'No se pudo cargar la configuración de pagos.'));
      } finally {
        if (!alive) return;
        setLoading(false);
      }
    };

    load();

    return () => {
      alive = false;
    };
  }, []);

  const canSave = useMemo(() => {
    // PayPal enabled => requiere al menos 1 identificador
    if (form.paypal_enabled) {
      const hasId = (form.paypal_client_id || '').trim().length > 3 || (form.paypal_merchant_id || '').trim().length > 3;
      if (!hasId) return false;
      if (form.paypal_mode !== 'sandbox' && form.paypal_mode !== 'live') return false;
    }

    // Transfer enabled => requiere datos mínimos
    if (form.bank_transfer_enabled) {
      if ((form.bank_holder_name || '').trim().length < 3) return false;
      if ((form.bank_name || '').trim().length < 2) return false;
      if (((form.bank_clabe || '').replace(/\D+/g, '')).length !== 18) return false;
    }

    return true;
  }, [form]);

  const setField = (key: keyof PaymentSettings, value: any) => {
    setSuccess(null);
    setError(null);

    if (key === 'bank_clabe') value = String(value || '').replace(/\D+/g, '').slice(0, 18);

    setForm(prev => ({ ...prev, [key]: value }));
  };

  const toggle = (key: 'paypal_enabled' | 'bank_transfer_enabled') => {
    setField(key, !form[key]);
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    if (!canSave) {
      setError(safeT('payments.invalid_form', 'Revisa los campos requeridos.'));
      return;
    }

    setSaving(true);
    try {
      const payload: PaymentSettings = {
        ...form,
        bank_clabe: form.bank_clabe ? form.bank_clabe.replace(/\D+/g, '') : form.bank_clabe
      };
      await PaymentsService.updateAdminPaymentSettings(payload);
      setSuccess(safeT('payments.saved_ok', 'Configuración de pagos guardada.'));
    } catch {
      setError(safeT('payments.saved_error', 'No se pudo guardar la configuración de pagos.'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div>{safeT('common.loading', 'Cargando...')}</div>;

  return (
    <div style={{ padding: 24, maxWidth: 980, margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
        <FiCreditCard style={{ fontSize: 22 }} />
        <h1 style={{ margin: 0, fontSize: '1.6rem' }}>
          {safeT('payments.title', 'Configuración de pagos')}
        </h1>
      </div>

      <p style={{ marginTop: 0, color: '#666' }}>
        {safeT('payments.subtitle', 'Define cómo se reciben pagos (PayPal) y si se mostrará transferencia como alternativa.')}
      </p>

      {error ? (
        <div style={{
          background: '#ffebee',
          border: '1px solid #ffcdd2',
          color: '#b71c1c',
          padding: 12,
          borderRadius: 10,
          marginBottom: 12,
          display: 'flex',
          alignItems: 'flex-start',
          gap: 10
        }}>
          <FiAlertCircle style={{ marginTop: 2 }} />
          <div>{error}</div>
        </div>
      ) : null}

      {success ? (
        <div style={{
          background: '#e8f5e9',
          border: '1px solid #c8e6c9',
          color: '#1b5e20',
          padding: 12,
          borderRadius: 10,
          marginBottom: 12
        }}>
          {success}
        </div>
      ) : null}

      <form onSubmit={onSubmit} style={{
        background: '#fff',
        border: '1px solid #eee',
        borderRadius: 14,
        padding: 18,
        boxShadow: '0 10px 30px rgba(0,0,0,0.06)'
      }}>
        {/* PayPal */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 14 }}>
          <h2 style={{ margin: 0 }}>{safeT('payments.paypal_section', 'PayPal')}</h2>
          <button
            type="button"
            onClick={() => toggle('paypal_enabled')}
            style={{
              border: 'none',
              background: 'transparent',
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
              fontWeight: 800,
              color: form.paypal_enabled ? '#2e7d32' : '#666'
            }}
          >
            {form.paypal_enabled ? <FiToggleRight size={28} /> : <FiToggleLeft size={28} />}
            {form.paypal_enabled ? safeT('payments.enabled', 'Activo') : safeT('payments.disabled', 'Inactivo')}
          </button>
        </div>

        <div style={{ marginTop: 12, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, opacity: form.paypal_enabled ? 1 : 0.55 }}>
          <div>
            <label style={{ fontWeight: 700 }}>{safeT('payments.paypal_client_id', 'PayPal Client ID')}</label>
            <input
              value={form.paypal_client_id || ''}
              onChange={e => setField('paypal_client_id', e.target.value)}
              disabled={!form.paypal_enabled}
              placeholder="AQ... (Client ID)"
              style={{ width: '100%', marginTop: 8, padding: 12, borderRadius: 10, border: '1px solid #ddd' }}
            />
          </div>

          <div>
            <label style={{ fontWeight: 700 }}>{safeT('payments.paypal_merchant_id', 'PayPal Merchant ID')}</label>
            <input
              value={form.paypal_merchant_id || ''}
              onChange={e => setField('paypal_merchant_id', e.target.value)}
              disabled={!form.paypal_enabled}
              placeholder="ABCDEF..."
              style={{ width: '100%', marginTop: 8, padding: 12, borderRadius: 10, border: '1px solid #ddd' }}
            />
          </div>

          <div>
            <label style={{ fontWeight: 700 }}>{safeT('payments.paypal_mode', 'Modo')}</label>
            <select
              value={form.paypal_mode}
              onChange={e => setField('paypal_mode', e.target.value as PayPalMode)}
              disabled={!form.paypal_enabled}
              style={{ width: '100%', marginTop: 8, padding: 12, borderRadius: 10, border: '1px solid #ddd' }}
            >
              <option value="sandbox">{safeT('payments.paypal_sandbox', 'Sandbox')}</option>
              <option value="live">{safeT('payments.paypal_live', 'Live')}</option>
            </select>
          </div>

          <div>
            <label style={{ fontWeight: 700 }}>{safeT('payments.webhook_url', 'Webhook URL')}</label>
            <input
              value={`${window.location.origin}/api/paypal/webhook`}
              disabled
              style={{ width: '100%', marginTop: 8, padding: 12, borderRadius: 10, border: '1px solid #eee', background: '#fafafa', color: '#666' }}
            />
          </div>
        </div>

        <hr style={{ margin: '18px 0', border: 'none', borderTop: '1px solid #eee' }} />

        {/* Transfer */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 14 }}>
          <h2 style={{ margin: 0 }}>{safeT('payments.bank_transfer_section', 'Transferencia bancaria (opcional)')}</h2>
          <button
            type="button"
            onClick={() => toggle('bank_transfer_enabled')}
            style={{
              border: 'none',
              background: 'transparent',
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
              fontWeight: 800,
              color: form.bank_transfer_enabled ? '#2e7d32' : '#666'
            }}
          >
            {form.bank_transfer_enabled ? <FiToggleRight size={28} /> : <FiToggleLeft size={28} />}
            {form.bank_transfer_enabled ? safeT('payments.enabled', 'Activo') : safeT('payments.disabled', 'Inactivo')}
          </button>
        </div>

        <div style={{ marginTop: 12, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, opacity: form.bank_transfer_enabled ? 1 : 0.55 }}>
          <div>
            <label style={{ fontWeight: 700 }}>{safeT('payments.bank_holder_name', 'Titular')}</label>
            <input
              value={form.bank_holder_name || ''}
              onChange={e => setField('bank_holder_name', e.target.value)}
              disabled={!form.bank_transfer_enabled}
              placeholder={safeT('payments.bank_holder_placeholder', 'Nombre del titular')}
              style={{ width: '100%', marginTop: 8, padding: 12, borderRadius: 10, border: '1px solid #ddd' }}
            />
          </div>

          <div>
            <label style={{ fontWeight: 700 }}>{safeT('payments.bank_name', 'Banco')}</label>
            <input
              value={form.bank_name || ''}
              onChange={e => setField('bank_name', e.target.value)}
              disabled={!form.bank_transfer_enabled}
              placeholder="BBVA, Banorte..."
              style={{ width: '100%', marginTop: 8, padding: 12, borderRadius: 10, border: '1px solid #ddd' }}
            />
          </div>

          <div>
            <label style={{ fontWeight: 700 }}>{safeT('payments.bank_clabe', 'CLABE')}</label>
            <input
              value={form.bank_clabe || ''}
              onChange={e => setField('bank_clabe', e.target.value)}
              disabled={!form.bank_transfer_enabled}
              inputMode="numeric"
              placeholder="18 dígitos"
              style={{ width: '100%', marginTop: 8, padding: 12, borderRadius: 10, border: '1px solid #ddd' }}
            />
          </div>

          <div>
            <label style={{ fontWeight: 700 }}>{safeT('payments.bank_reference_hint', 'Referencia / Instrucciones')}</label>
            <input
              value={form.bank_reference_hint || ''}
              onChange={e => setField('bank_reference_hint', e.target.value)}
              disabled={!form.bank_transfer_enabled}
              placeholder={safeT('payments.bank_reference_placeholder', 'Ej. Usa tu ID como referencia')}
              style={{ width: '100%', marginTop: 8, padding: 12, borderRadius: 10, border: '1px solid #ddd' }}
            />
          </div>
        </div>

        <button
          type="submit"
          disabled={saving || !canSave}
          style={{
            marginTop: 16,
            width: '100%',
            padding: 14,
            borderRadius: 12,
            border: 'none',
            background: 'linear-gradient(135deg, #673ab7 0%, #512da8 100%)',
            color: '#fff',
            fontWeight: 800,
            cursor: saving || !canSave ? 'not-allowed' : 'pointer',
            opacity: saving || !canSave ? 0.75 : 1,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 10
          }}
        >
          <FiSave />
          {saving ? safeT('common.saving', 'Guardando...') : safeT('common.save_changes', 'Guardar Cambios')}
        </button>
      </form>
    </div>
  );
};

export default AdminPaymentSettingsPage;
